<template>
  <div class="main-wrapper">
    <layoutheader></layoutheader>
    <breadcrumb-doctor title="Appointment history" text="Doctor" text1="Appointment history" />
    <div class="content">
      <div class="container">
        <div class="row">
          <doctorsidebar></doctorsidebar>
          <div class="col-md-7 col-lg-8 col-xl-9 adm-scope hist">

            <!-- Search & filters -->
            <div class="adm-card hist-filters">
              <div class="adm-search hist-search">
                <i class="fas fa-search"></i>
                <input v-model="q" class="adm-input" placeholder="Search patient name, phone, email or ID" aria-label="Search appointments" />
              </div>
              <select v-model="period" class="adm-select hist-select" aria-label="Period" @change="onPeriod">
                <option value="all">Any time</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
                <option value="365">Last 12 months</option>
                <option value="custom">Custom dates…</option>
              </select>
              <select v-if="clinics.length > 1" v-model="clinicid" class="adm-select hist-select" aria-label="Clinic" @change="reload">
                <option value="">All clinics</option>
                <option v-for="c in clinics" :key="c.clinicid" :value="c.clinicid">{{ c.clinicname }}</option>
              </select>
              <template v-if="period === 'custom'">
                <input v-model="start" type="date" class="adm-input hist-date" aria-label="From" :max="end || undefined" @change="reload" />
                <input v-model="end" type="date" class="adm-input hist-date" aria-label="To" :min="start || undefined" @change="reload" />
              </template>
              <button v-if="hasFilters" type="button" class="adm-btn adm-btn--ghost adm-btn--sm" @click="clearFilters">Clear</button>
            </div>

            <div class="adm-tabs hist-tabs" role="tablist" aria-label="Outcome">
              <button v-for="t in tabs" :key="t.value" type="button" class="adm-tab" role="tab"
                      :class="{ 'is-active': outcome === t.value }" :aria-selected="outcome === t.value" @click="setOutcome(t.value)">
                {{ t.label }} <span class="adm-tab__count">{{ countFor(t.value) }}</span>
              </button>
            </div>

            <div v-if="outcome === 'E' && countFor('E') > 0" class="adm-alert adm-alert--info">
              <i class="fas fa-info-circle"></i>
              <div>These appointments are in the past but were never marked. Mark each as <b>Visited</b> or <b>No-show</b> to keep your reports accurate.</div>
            </div>
            <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>

            <div class="adm-card">
              <div v-if="loading && !rows.length">
                <div v-for="n in 6" :key="n" class="adm-list-row"><div class="adm-skeleton" style="width: 55%"></div></div>
              </div>
              <div v-else-if="!rows.length" class="adm-empty">
                <i class="fas fa-calendar-times"></i>
                <strong>No appointments found</strong>
                {{ hasFilters || outcome ? "Try a different search, period or tab." : "Past appointments will show up here." }}
              </div>
              <template v-else>
                <div v-for="group in grouped" :key="group.date" class="hist-day">
                  <div class="hist-day__head">{{ dayLabel(group.date) }} <span class="adm-muted">· {{ group.items.length }}</span></div>
                  <div v-for="a in group.items" :key="a.slno" class="hist-row" role="button" tabindex="0"
                       @click="open(a)" @keydown.enter="open(a)">
                    <span class="hist-time">{{ time(a.appointmenttime) }}</span>
                    <span class="adm-avatar adm-avatar--sm">{{ initials(name(a)) }}</span>
                    <div class="hist-who">
                      <span class="adm-person__name">{{ name(a) }}</span>
                      <span class="adm-person__meta">
                        {{ [genderLabel(a.gender), a.age ? `${a.age} yrs` : "", a.contactnumbers].filter(Boolean).join(" · ") }}
                      </span>
                    </div>
                    <div class="hist-reason" :title="a.reason || ''">
                      <span>{{ a.appointmentstatus === "C" ? (a.cancelreason || "Cancelled") : (a.reason || "—") }}</span>
                      <span v-if="clinics.length > 1" class="adm-person__meta">{{ a.clinicname }}</span>
                    </div>
                    <div class="hist-status" @click.stop>
                      <template v-if="a.appointmentstatus === 'E'">
                        <button type="button" class="adm-btn adm-btn--sm hist-mark" :disabled="saving === a.slno" @click="mark(a, 'V')">
                          <i class="fas fa-check"></i> Visited
                        </button>
                        <button type="button" class="adm-btn adm-btn--sm" :disabled="saving === a.slno" @click="mark(a, 'A')">No-show</button>
                      </template>
                      <span v-else class="adm-badge" :class="STATUS[a.appointmentstatus].badge">{{ STATUS[a.appointmentstatus].label }}</span>
                    </div>
                  </div>
                </div>
                <div class="adm-pagination">
                  <span>Showing {{ (pagination.page - 1) * pagination.per_page + 1 }}–{{ Math.min(pagination.page * pagination.per_page, pagination.total) }} of {{ pagination.total }}</span>
                  <div class="adm-pagination__pages">
                    <button type="button" class="adm-btn adm-btn--sm" :disabled="loading || !pagination.has_prev" @click="load(pagination.page - 1)"><i class="fas fa-arrow-left"></i> Newer</button>
                    <button type="button" class="adm-btn adm-btn--sm" :disabled="loading || !pagination.has_next" @click="load(pagination.page + 1)">Older <i class="fas fa-arrow-right"></i></button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Details -->
    <admin-modal v-if="selected" :title="name(selected)" :subtitle="`${dayLabel(selected.appointmentdate)} at ${time(selected.appointmenttime)}`" @close="selected = null">
      <dl class="adm-dl">
        <dt>Status</dt>
        <dd><span class="adm-badge" :class="STATUS[selected.appointmentstatus].badge">{{ STATUS[selected.appointmentstatus].label }}</span></dd>
        <dt>Reason for visit</dt><dd>{{ selected.reason || "—" }}</dd>
        <template v-if="selected.appointmentstatus === 'C'"><dt>Cancel reason</dt><dd>{{ selected.cancelreason || "—" }}</dd></template>
        <dt>Clinic</dt><dd>{{ selected.clinicname || selected.clinicid }}{{ selected.locationname ? `, ${selected.locationname}` : "" }}</dd>
        <dt>Patient</dt><dd>{{ [genderLabel(selected.gender), selected.age ? `${selected.age} yrs` : "", `ID ${selected.patientid}`].filter(Boolean).join(" · ") }}</dd>
        <dt>Phone</dt><dd>{{ selected.contactnumbers || "—" }}</dd>
        <dt>Email</dt><dd>{{ selected.email || "—" }}</dd>
        <dt>Visits with you</dt><dd>{{ selected.visits }}</dd>
        <dt>Appointment no.</dt><dd>{{ selected.slno }}</dd>
      </dl>
      <template #footer>
        <template v-if="selected.appointmentstatus === 'E'">
          <button type="button" class="adm-btn" :disabled="saving === selected.slno" @click="mark(selected, 'A')">Mark no-show</button>
          <button type="button" class="adm-btn adm-btn--success" :disabled="saving === selected.slno" @click="mark(selected, 'V')"><i class="fas fa-check"></i> Mark visited</button>
        </template>
        <router-link :to="`/patient-profile/${selected.patientid}`" class="adm-btn adm-btn--primary">Patient profile <i class="fas fa-arrow-right"></i></router-link>
      </template>
    </admin-modal>

    <doctorfooter />
  </div>
</template>

<script>
import axios from "axios";
import "../admin/admin.css";
import AdminModal from "../admin/components/AdminModal.vue";
import { errorMessage, initials } from "../admin/admin-utils";

const STATUS = {
  V: { label: "Visited", badge: "adm-badge--active" },
  A: { label: "No-show", badge: "adm-badge--rejected" },
  C: { label: "Cancelled", badge: "adm-badge--inactive" },
  E: { label: "Not marked", badge: "adm-badge--pending" },
};

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default {
  name: "doctor-appointment-history",
  components: { AdminModal },
  data() {
    return {
      STATUS,
      q: "",
      period: "all",
      start: "",
      end: "",
      clinicid: "",
      clinics: [],
      outcome: "",
      rows: [],
      counts: {},
      pagination: { total: 0, page: 1, per_page: 20, has_next: false, has_prev: false },
      loading: true,
      error: "",
      saving: null,
      selected: null,
      requestId: 0,
      tabs: [
        { value: "", label: "All" },
        { value: "V", label: "Visited" },
        { value: "A", label: "No-show" },
        { value: "C", label: "Cancelled" },
        { value: "E", label: "Not marked" },
      ],
    };
  },
  computed: {
    doctorId() {
      return sessionStorage.getItem("userdbid");
    },
    hasFilters() {
      return !!(this.q.trim() || this.period !== "all" || this.clinicid);
    },
    grouped() {
      const groups = [];
      this.rows.forEach(row => {
        const last = groups[groups.length - 1];
        if (last && last.date === row.appointmentdate) last.items.push(row);
        else groups.push({ date: row.appointmentdate, items: [row] });
      });
      return groups;
    },
    range() {
      if (this.period === "all") return {};
      if (this.period === "custom") return { start: this.start || undefined, end: this.end || undefined };
      return { start: isoDaysAgo(Number(this.period)) };
    },
  },
  watch: {
    q() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.reload(), 350);
    },
  },
  methods: {
    initials,
    name(a) {
      return [a.firstname, a.lastname].filter(Boolean).join(" ").trim() || `Patient ${a.patientid}`;
    },
    genderLabel(g) {
      return { M: "Male", F: "Female", O: "Other" }[(g || "").trim()] || "";
    },
    time(value) {
      if (!value) return "";
      const [h, m] = String(value).split(":").map(Number);
      return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
    },
    dayLabel(value) {
      const d = new Date(`${value}T00:00:00`);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const diff = Math.round((today - d) / 86400000);
      if (diff === 0) return "Today";
      if (diff === 1) return "Yesterday";
      return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    },
    countFor(value) {
      const c = value ? this.counts[value] : this.counts.total;
      return c === undefined ? "–" : c;
    },
    setOutcome(value) {
      this.outcome = value;
      this.load(1);
    },
    onPeriod() {
      if (this.period !== "custom") this.reload();
    },
    clearFilters() {
      this.q = "";
      this.period = "all";
      this.start = "";
      this.end = "";
      this.clinicid = "";
      this.reload();
    },
    reload() {
      this.load(1);
    },
    async load(page = 1) {
      clearTimeout(this.timer);
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = "";
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/appointment-history`, {
          params: {
            q: this.q.trim() || undefined,
            outcome: this.outcome || undefined,
            clinicid: this.clinicid || undefined,
            ...this.range,
            page,
            per_page: 20,
          },
        });
        if (requestId !== this.requestId) return;
        this.rows = data.appointments;
        this.counts = data.counts;
        this.pagination = data.pagination;
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.rows = [];
        this.error = errorMessage(error, "Could not load appointments.");
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async mark(a, statusCode) {
      this.saving = a.slno;
      try {
        await axios.put(`/patients/${a.patientid}/appointments/${a.slno}/update`, null, { params: { status_data: statusCode } });
        // Update in place so the list does not jump; refresh the tab counts quietly.
        a.appointmentstatus = statusCode;
        if (statusCode === "V") a.visits += 1;
        this.counts = { ...this.counts, E: this.counts.E - 1, [statusCode]: (this.counts[statusCode] || 0) + 1 };
      } catch (error) {
        this.error = errorMessage(error, "Could not update the appointment.");
      } finally {
        this.saving = null;
      }
    },
    open(a) {
      this.selected = a;
    },
    async loadClinics() {
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/clinicids`, { params: { details: true } });
        this.clinics = data || [];
      } catch (error) {
        this.clinics = [];
      }
    },
  },
  created() {
    this.loadClinics();
    this.load(1);
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
};
</script>

<style scoped>
.hist {
  font-size: 14px;
}

.hist-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px;
  margin-bottom: 14px;
}

.hist-search {
  flex: 1 1 260px;
  max-width: none;
  min-width: 200px;
}

.hist-select {
  width: auto;
  min-width: 150px;
  padding: 8px 10px;
}

.hist-date {
  width: 150px;
  padding: 8px 10px;
}

.hist-tabs {
  margin-bottom: 14px;
}

.hist-day__head {
  padding: 10px 16px;
  background: #f9fafb;
  border-bottom: 1px solid var(--adm-line-2);
  font-size: 12px;
  font-weight: 700;
  color: var(--adm-ink-2);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.hist-day:first-child .hist-day__head {
  border-radius: var(--adm-radius) var(--adm-radius) 0 0;
}

.hist-row {
  display: grid;
  grid-template-columns: 70px 32px minmax(0, 1.3fr) minmax(0, 1fr) 190px;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--adm-line-2);
  cursor: pointer;
}

.hist-row:hover {
  background: #fcfcfd;
}

.hist-row:focus-visible {
  outline: 2px solid var(--adm-primary);
  outline-offset: -2px;
}

.hist-time {
  font-weight: 700;
  color: var(--adm-ink);
  font-variant-numeric: tabular-nums;
  font-size: 13px;
}

.hist-who,
.hist-reason {
  min-width: 0;
}

.hist-who .adm-person__name,
.hist-who .adm-person__meta,
.hist-reason span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hist-reason {
  color: var(--adm-ink-2);
  font-size: 13px;
}

.hist-status {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

.hist-mark {
  border-color: #abefc6;
  color: var(--adm-success-700);
}

@media (max-width: 767.98px) {
  .hist-row {
    grid-template-columns: 56px minmax(0, 1fr) 170px;
  }

  .hist-row .adm-avatar,
  .hist-reason {
    display: none;
  }
}
</style>
